/**
 * Approvals by stage — prototype VIEWS.approvals + openApproval + moveAp + newApproval (docs/prototype.html
 * 1048–1076), shown from All work → Approvals → "Show as a board by stage" (prototype `boardOn`, hub mode: no
 * page head, only its "+ New item" action). Four stats, managers' "View as Team | Mine", one column per stage
 * (dot colours as the prototype), cards with type chip, vN, days in stage and the owner.
 *
 * Data: the same Document rows as Clients → Documents. GET /documents (owner=me for "Mine"; a member sees their own,
 * as the prototype's board), its `stats` on Team (on Mine they are counted from its own cards — the server's stats
 * have no owner filter); the drawer GET /documents/{id} (version history), POST …/stage
 * ("Move to"; Changes requested opens the next revision on the server), POST …/versions (a new file),
 * PATCH /documents/{id} (the "Latest email" line), GET …/versions/{vid}/file ("Open"). "+ New item" is
 * POST /documents without a file.
 */
import { useState } from 'react';
import { DOC_STAGE_LABEL, type DocType, type DocStage, type DocumentListItem, type DocumentPage } from '@amplispot/contracts';
import {
  DOC_NAME_MAX,
  DOC_NAME_MIN,
  STAGE_DOT,
  STAGE_ORDER,
  TYPE_CHIP,
  openDocumentFile,
  useAddVersion,
  useCreateDocument,
  useDocument,
  useDocuments,
  useMoveDocument,
  useUpdateDocument,
} from '../../lib/documents';
import { useClients } from '../../lib/data';
import { useSession } from '../../lib/session';
import { todayIso } from '../../lib/format';
import { mondayOf } from '../../lib/timesheets';
import { Avatar, Drawer, ErrorState, Field, Loading, Modal, Seg, errorText, useToast } from '../../ui/kit';
import { Stat } from '../timesheets/TimesheetReport';

type ViewAs = 'all' | 'mine';
/** Prototype `newApproval` offers these four (an RCA is uploaded, not approved). */
const NEW_ITEM_TYPES: DocType[] = ['MIS', 'Deck', 'Report', 'Plan'];

/** `clientId`: All work's client filter — the board, its stats and "+ New item" follow it. */
export function ApprovalsBoard({ clientId }: { clientId?: string } = {}) {
  const { can, readOnly } = useSession();
  const mgr = can('clients.read.all');
  const [viewAs, setViewAs] = useState<ViewAs>('all');
  const owner = mgr && viewAs === 'all' ? 'all' : 'me';
  const q = useDocuments({ owner, ...(clientId ? { client_id: clientId } : {}) });
  const [open, setOpen] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const write = !readOnly && (mgr || can('clients.read.own'));

  return (
    <>
      {write ? (
        <div className="ph" style={{ margin: '0 0 12px', justifyContent: 'flex-end' }}>
          <div className="r" style={{ marginLeft: 0 }}>
            <button className="btn pri" onClick={() => setAdding(true)}>
              + New item
            </button>
          </div>
        </div>
      ) : null}
      {q.isPending ? (
        <Loading label="Loading the approvals…" />
      ) : q.isError ? (
        <ErrorState error={q.error} onRetry={() => void q.refetch()} />
      ) : (
        <>
          <BoardStats stats={owner === 'me' ? statsOf(q.data.rows) : q.data.stats} />
          {mgr ? (
            <div className="bar">
              <span className="lbl">View as</span>
              <Seg<ViewAs>
                options={[
                  ['all', 'Team'],
                  ['mine', 'Mine'],
                ]}
                value={viewAs}
                onChange={setViewAs}
              />
            </div>
          ) : null}
          <Board rows={q.data.rows} onOpen={setOpen} />
        </>
      )}
      {open ? <ApprovalDrawer id={open} onClose={() => setOpen(null)} /> : null}
      {adding ? <NewItemModal onClose={() => setAdding(false)} clientId={clientId} /> : null}
    </>
  );
}

type DocStats = DocumentPage['stats'];

/**
 * The server's `stats` cover every document the caller sees (no owner filter), so on "Mine" — and for a member,
 * whose board is always their own — they are counted from the cards shown, with the server's rules: in flight =
 * not approved, stuck = `is_stuck`, approved this week = approved on or after this IST Monday.
 */
function statsOf(rows: DocumentListItem[]): DocStats {
  const monday = mondayOf(todayIso());
  const n = (f: (d: DocumentListItem) => boolean) => rows.filter(f).length;
  return {
    in_flight: n((d) => d.stage !== 'approved'),
    with_client: n((d) => d.stage === 'with_client'),
    with_client_stuck: n((d) => d.stage === 'with_client' && d.is_stuck),
    changes_requested: n((d) => d.stage === 'changes_requested'),
    approved_this_week: n((d) => d.stage === 'approved' && d.stage_since >= monday),
  };
}

function BoardStats({ stats }: { stats: DocStats }) {
  return (
    <div className="stats">
      <Stat k="In flight" v={stats.in_flight} />
      <Stat
        k="With client"
        v={stats.with_client}
        s={`${stats.with_client_stuck} waiting 3+ days`}
        cls={stats.with_client_stuck ? 'amber' : ''}
      />
      <Stat k="Changes requested" v={stats.changes_requested} cls="amber" />
      <Stat k="Approved this week" v={stats.approved_this_week} cls="green" />
    </div>
  );
}

function Board({ rows, onOpen }: { rows: DocumentListItem[]; onOpen: (id: string) => void }) {
  return (
    <div className="kan">
      {STAGE_ORDER.map((k) => {
        const c = rows.filter((a) => a.stage === k);
        return (
          <div className="kcol" key={k}>
            <div className="kh">
              <span className={`dot ${STAGE_DOT[k].cls}`} style={STAGE_DOT[k].style} />
              {DOC_STAGE_LABEL[k]}
              <span className="n">{c.length}</span>
            </div>
            {c.map((a) => (
              <div
                key={a.id}
                className="kc"
                onClick={() => onOpen(a.id)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), onOpen(a.id))}
              >
                <div className="t">{a.name}</div>
                <div style={{ marginTop: 3, color: 'var(--sub)', fontSize: 12 }}>{a.client.name}</div>
                <div className="m">
                  <span className={`chip ${TYPE_CHIP[a.doc_type]}`}>{a.doc_type}</span>
                  <span className="mono">v{a.revision}</span>
                  <span>{a.days_in_stage}d in stage</span>
                  <span style={{ marginLeft: 'auto' }}>
                    <Avatar user={a.owner} />
                  </span>
                </div>
              </div>
            ))}
            {!c.length ? <div style={{ color: 'var(--mute)', fontSize: 12.5, padding: 8 }}>Empty</div> : null}
          </div>
        );
      })}
    </div>
  );
}

/** Prototype `openApproval`: stage / version / days chips, owner, latest email, version history, Move to. */
function ApprovalDrawer({ id, onClose }: { id: string; onClose: () => void }) {
  const q = useDocument(id);
  const { readOnly } = useSession();
  const move = useMoveDocument();
  const addVersion = useAddVersion();
  const update = useUpdateDocument();
  const toast = useToast();
  const [editMail, setEditMail] = useState<string | null>(null);

  if (q.isPending)
    return (
      <Drawer title="Approval" onClose={onClose}>
        <Loading />
      </Drawer>
    );
  if (q.isError)
    return (
      <Drawer title="Approval" onClose={onClose}>
        <ErrorState error={q.error} onRetry={() => void q.refetch()} />
      </Drawer>
    );
  const a = q.data;
  const edit = a.can_edit && !readOnly;
  const busy = move.isPending || addVersion.isPending || update.isPending;

  const moveTo = (stage: DocStage) =>
    move.mutate(
      { id: a.id, version: a.version, stage },
      {
        onSuccess: () => {
          toast(`Moved to ${DOC_STAGE_LABEL[stage]}`);
          onClose();
        },
        onError: (e) => toast(errorText(e)),
      },
    );
  const upload = (file: File) =>
    addVersion.mutate(
      { id: a.id, file },
      {
        onSuccess: (d) => {
          toast(`Version ${d.revision} uploaded`);
        },
        onError: (e) => toast(errorText(e)),
      },
    );
  const saveMail = () =>
    update.mutate(
      { id: a.id, version: a.version, body: { last_sent_note: editMail?.trim() ? editMail.trim() : null } },
      {
        onSuccess: () => {
          setEditMail(null);
          toast('Saved');
        },
        onError: (e) => toast(errorText(e)),
      },
    );
  const open = (vid: string) => openDocumentFile(a.id, vid).catch((e) => toast(errorText(e)));
  const current = a.versions[0];

  return (
    <Drawer title={`${a.doc_type} · ${a.client.name}`} onClose={onClose}>
      <h2 style={{ fontSize: 18 }}>{a.name}</h2>
      <div style={{ margin: '8px 0 16px', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <span className="chip brand">{DOC_STAGE_LABEL[a.stage]}</span>
        <span className="chip">Version {a.revision}</span>
        <span className="chip">
          {a.days_in_stage} day{a.days_in_stage === 1 ? '' : 's'} in stage
        </span>
      </div>
      <dl className="kv">
        <dt>Owner</dt>
        <dd>{a.owner.name}</dd>
        <dt>Latest email</dt>
        <dd>
          {editMail != null ? (
            <span className="split" style={{ gap: 6 }}>
              <input className="in" value={editMail} onChange={(e) => setEditMail(e.target.value)} maxLength={300} placeholder="e.g. Sent 1 Oct, no reply" aria-label="Latest email" />
              <button className="btn sm pri" onClick={saveMail} disabled={busy}>
                Save
              </button>
              <button className="btn sm ghost" onClick={() => setEditMail(null)}>
                Cancel
              </button>
            </span>
          ) : (
            <>
              {a.last_sent_note ?? 'Not sent yet'}
              {edit ? (
                <>
                  {' '}
                  <button className="btn sm ghost" onClick={() => setEditMail(a.last_sent_note ?? '')}>
                    Change
                  </button>
                </>
              ) : null}
            </>
          )}
        </dd>
      </dl>

      <div className="lbl" style={{ margin: '20px 0 8px' }}>
        Version history
      </div>
      <div style={{ display: 'grid', gap: 8, fontSize: 13 }}>
        {a.versions.map((v) => (
          <div className="split" key={v.id}>
            <span className="chip">v{v.revision}</span>
            <span>
              {v.note ?? (v.id === current?.id ? 'Current' : v.revision === 1 ? 'First draft' : 'Revised after feedback')}
              {v.has_file ? null : <span className="sub"> · no file yet</span>}
            </span>
            <div className="grow" />
            <button className="btn sm ghost" onClick={() => void open(v.id)} disabled={!v.has_file}>
              Open
            </button>
          </div>
        ))}
        {!a.versions.length ? <span className="sub">No versions yet.</span> : null}
      </div>

      {edit ? (
        <>
          <div style={{ marginTop: 10 }}>
            <label className={`btn sm ghost${busy ? ' dis' : ''}`} style={{ cursor: busy ? 'default' : 'pointer' }}>
              {addVersion.isPending ? 'Uploading…' : current && !current.has_file ? `Upload version ${a.revision}` : 'Upload a new version'}
              <input
                type="file"
                hidden
                disabled={busy}
                aria-label="New version file"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = '';
                  if (f) upload(f);
                }}
              />
            </label>
          </div>

          <div className="lbl" style={{ margin: '20px 0 8px' }}>
            Move to
          </div>
          <div className="split" style={{ flexWrap: 'wrap' }}>
            {STAGE_ORDER.filter((s) => s !== a.stage).map((s) => (
              <button key={s} className="btn sm" onClick={() => moveTo(s)} disabled={busy}>
                {DOC_STAGE_LABEL[s]}
              </button>
            ))}
          </div>
        </>
      ) : null}
    </Drawer>
  );
}

/**
 * Prototype `newApproval`: Title, Client, Type → "Add item". It starts in Drafting, owned by the caller.
 * Opened from the Approvals board and from Work's '+ Add' chooser ("An item for approval"), which passes the client
 * the list is filtered to.
 */
export function NewItemModal({ onClose, clientId: initialClient }: { onClose: () => void; clientId?: string }) {
  const clients = useClients();
  const create = useCreateDocument();
  const toast = useToast();
  const [title, setTitle] = useState('');
  const [client, setClient] = useState(initialClient ?? '');
  const [type, setType] = useState<DocType>('MIS');
  const [err, setErr] = useState<string | undefined>();
  const list = clients.data ?? [];
  const clientId = client || list[0]?.id || '';

  const save = () => {
    if (!title.trim()) {
      setErr('Add a title');
      return toast('Add a title');
    }
    if (title.trim().length < DOC_NAME_MIN) {
      setErr(`Give it a name of at least ${DOC_NAME_MIN} characters`);
      return toast(`Give it a name of at least ${DOC_NAME_MIN} characters`);
    }
    if (!clientId) return toast('Pick a client');
    create.mutate(
      { body: { client_id: clientId, name: title.trim(), doc_type: type } },
      {
        onSuccess: () => {
          toast('Added to Drafting');
          onClose();
        },
        onError: (e) => toast(errorText(e)),
      },
    );
  };

  return (
    <Modal
      title="New item for approval"
      sub="It starts in Drafting."
      onClose={onClose}
      footer={
        <>
          <div className="grow" />
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn pri" onClick={save} disabled={create.isPending}>
            Add item
          </button>
        </>
      }
    >
      <Field label="Title" error={err}>
        <input
          className="in"
          placeholder="e.g. October MIS"
          value={title}
          maxLength={DOC_NAME_MAX}
          onChange={(e) => {
            setTitle(e.target.value);
            setErr(undefined);
          }}
        />
      </Field>
      <div className="cols c2">
        <Field label="Client">
          <select className="in" value={clientId} onChange={(e) => setClient(e.target.value)} disabled={clients.isPending}>
            {clients.isPending ? <option>Loading clients…</option> : null}
            {list.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Type">
          <select className="in" value={type} onChange={(e) => setType(e.target.value as DocType)}>
            {NEW_ITEM_TYPES.map((o) => (
              <option key={o}>{o}</option>
            ))}
          </select>
        </Field>
      </div>
    </Modal>
  );
}
